import { useState } from "react";


export interface IPaginationQuery<T = string> {
  page: number;
  sort_by?: T;
  sort_dir?: "asc" | "desc";
  per_page?: number;
}

interface IUsePaginationReturn<T = string> {
  currentPage: number;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  sortBy?: T;
  sortDir: "asc" | "desc";
  handleSortChange: (field: T) => void;
  query: IPaginationQuery<T>
}


interface IUsePaginationOptions<T = string> {
  initialPage?: number;
  initialSortBy?: T;
  initialSortDir?: "asc" | "desc";
  perPage?: number;
}

const usePagination = <T = string>({
  initialPage = 1,
  initialSortBy,
  initialSortDir = "desc",
  perPage = 10,
}: IUsePaginationOptions<T>): IUsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sortBy, setSortBy] = useState<T | undefined>(initialSortBy);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(initialSortDir);

  const goToPage = (page: number) => {
    setCurrentPage(() => (page < 1 ? 1 : page));
  };

  const nextPage = () => setCurrentPage((prev) => prev + 1);
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const handleSortChange = (field: T) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    goToPage(1);
  };

  return {
    currentPage,
    goToPage,
    nextPage,
    prevPage,
    sortBy,
    sortDir,
    handleSortChange,
    query: {
      page: currentPage,
      sort_by: sortBy,
      sort_dir: sortDir,
      per_page: perPage,
    },
  };
};


export default usePagination;